<script setup lang="ts">
import { computed, toRef } from 'vue';
import { useRouter } from 'vue-router';
import { useFlag } from '@loadsmart/vue-feature-flags';
import type { IWarehouse } from '@nova/core';

import CheckinStepFooter from '@/components/CheckinStepFooter.vue';
import IdVerification from '@/components/IdVerification.vue';
import { CheckinFlowStep, useCheckinFlow } from '@/composables/useCheckinFlow';
import { resolveIdVerificationSettings } from '@/composables/useIdVerificationSettings';
import { useCheckinSubmission } from '@/composables/useCheckinSubmission';

defineOptions({ name: 'IdVerificationPage' });

const props = defineProps<{
  warehouse: Partial<IWarehouse>;
}>();

const router = useRouter();
const checkinFlow = useCheckinFlow();
const warehouseRef = toRef(props, 'warehouse');
const { isSubmitting, inlineError, submit } = useCheckinSubmission(warehouseRef);
const rejectedAssetVisitFlag = useFlag('enable-rejected-asset-visit-status');

const idVerificationSettings = computed(() =>
  resolveIdVerificationSettings(props.warehouse, checkinFlow.loadTypeId.value)
);

function handleVerificationComplete(event: { inquiryId?: string }) {
  checkinFlow.setInquiryId(event.inquiryId ?? null);
  checkinFlow.setStepValid(CheckinFlowStep.IdVerification, true);
}

function handleVerificationSkip() {
  checkinFlow.setStepValid(CheckinFlowStep.IdVerification, true);
}

function handleVerificationError(event: { inquiryId?: string; message?: string }) {
  if (rejectedAssetVisitFlag.value?.enabled) {
    checkinFlow.setInquiryId(event.inquiryId ?? null);
    router.push({ name: 'rejected-check-in' });
    return;
  }

  router.push({ name: 'verification-error', query: { message: event.message ?? '' } });
}

function handlePrevious() {
  // This step now always follows the details form, so that is where back goes.
  checkinFlow.setPreviousStep(checkinFlow.previousStep.value ?? CheckinFlowStep.CheckinDetailsPage);
}

function handleNext() {
  submit(router);
}
</script>

<template>
  <div class="flex-grow-1 d-flex flex-column justify-space-between gap-6">
    <div v-if="inlineError" class="id-verification__error">
      <v-alert type="warning" icon="mdi-alert-circle-outline" prominent>
        {{ inlineError.title }}
      </v-alert>
    </div>

    <id-verification
      :warehouse="warehouse"
      :required="idVerificationSettings.isRequired"
      @complete="handleVerificationComplete"
      @skip="handleVerificationSkip"
      @error="handleVerificationError" />

    <checkin-step-footer
      :next-disabled="!checkinFlow.isStepValid(CheckinFlowStep.IdVerification)"
      :next-loading="isSubmitting"
      @previous="handlePrevious"
      @next="handleNext" />
  </div>
</template>
